import { useMemo, useState, type DragEvent } from "react";
import { useReactFlow } from "@xyflow/react";
import { CalendarClock, Globe, Hand, Radio, Search } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Input, cn } from "@ncp/ui";
import { DRAG_MIME, type PaletteItem } from "../canvas/BuilderCanvas";
import { KIND_META } from "../canvas/nodes";
import { NODE_W } from "../graph";
import { useCatalog } from "../api";
import { useBuilder, useStore } from "../store";
import type { NodeKind } from "../types";

const STEPS: NodeKind[] = ["Task", "Decision", "Agent", "Automation", "Wait", "SubProcess", "End", "Start"];

function drag(item: PaletteItem) {
  return (e: DragEvent) => { e.dataTransfer.setData(DRAG_MIME, JSON.stringify(item)); e.dataTransfer.effectAllowed = "copy"; };
}

/** Left rail: steps, start triggers and actions to drag onto the canvas, and an outline of the workflow's states. */
export function Palette({ editable }: { editable: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const catalog = useCatalog();
  const graph = useBuilder((s) => s.graph);
  const selection = useBuilder((s) => s.selection);
  const store = useStore();
  const flow = useReactFlow();
  const [q, setQ] = useState("");
  const outline = useMemo(() => graph.nodes.filter((n) => !q || `${n.label} ${n.labelAr} ${n.key}`.toLowerCase().includes(q.toLowerCase())), [graph.nodes, q]);

  const focus = (id: string) => {
    const n = graph.nodes.find((x) => x.id === id);
    store.select({ nodes: [id], edges: [] });
    if (n?.position) flow.setCenter(n.position.x + NODE_W / 2, n.position.y + 40, { zoom: 1, duration: 450 });
  };

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col border-e border-sand-200 bg-white/80 backdrop-blur">
      {editable ? (
        <div className="space-y-4 overflow-y-auto border-b border-sand-200 p-3" style={{ maxHeight: "58%" }}>
          <Section title={t("admin:builder.palette.steps")} hint={t("admin:builder.palette.stepsHint")}>
            <div className="grid grid-cols-2 gap-1.5">
              {STEPS.map((k) => {
                const m = KIND_META[k];
                return (
                  <div key={k} draggable onDragStart={drag({ type: "node", kind: k })} className="group flex cursor-grab items-center gap-2 rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-xs shadow-sm transition hover:-translate-y-px hover:border-aegold-300 hover:shadow active:cursor-grabbing" title={t(`admin:builder.kindHints.${k}`)}>
                    <span className={cn("grid size-6 place-items-center rounded-md", m.chip)}><m.icon className="size-3.5" /></span>
                    <span className="truncate font-medium">{t(`admin:builder.kinds.${k}`)}</span>
                  </div>
                );
              })}
            </div>
          </Section>
          <Section title={t("admin:builder.palette.triggers")} hint={t("admin:builder.palette.triggersHint")}>
            <div className="grid grid-cols-2 gap-1.5">
              {([["manual", Hand], ["eservice", Globe], ["schedule", CalendarClock], ["event", Radio]] as const).map(([kind, Icon]) => (
                <div key={kind} draggable onDragStart={drag({ type: "trigger", kind })} className="flex cursor-grab items-center gap-2 rounded-lg border border-dashed border-success-300 bg-success-50/50 px-2 py-1.5 text-xs transition hover:border-success-500">
                  <Icon className="size-3.5 text-success-700" /><span className="truncate">{t(`admin:builder.triggerKinds.${kind}`)}</span>
                </div>
              ))}
            </div>
          </Section>
          <Section title={t("admin:builder.palette.actions")} hint={t("admin:builder.palette.actionsHint")}>
            <div className="space-y-1">
              {(catalog.data?.actions ?? []).map((a) => (
                <div key={a.type} draggable onDragStart={drag({ type: "action", actionType: a.type })} title={locale === "ar" ? a.descriptionAr : a.description} className="flex cursor-grab items-center gap-2 rounded-lg border border-seablue-200 bg-seablue-50/60 px-2 py-1 text-xs transition hover:border-seablue-400">
                  <span className="size-1.5 rounded-full bg-seablue-500" /><span className="truncate">{locale === "ar" ? a.labelAr : a.label}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col p-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-500">{t("admin:builder.palette.outline")} <span className="font-normal">· {graph.nodes.length}</span></p>
        <div className="relative mb-2"><Search className="pointer-events-none absolute start-2 top-2 size-3.5 text-sand-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 ps-7 text-xs" placeholder={t("admin:builder.palette.find")} /></div>
        <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
          {outline.map((n) => {
            const m = KIND_META[n.kind] ?? KIND_META.Task;
            return (
              <li key={n.id}>
                <button type="button" onClick={() => focus(n.id!)} className={cn("flex w-full items-center gap-2 rounded-md px-2 py-1 text-start text-xs transition hover:bg-sand-100", selection.nodes.includes(n.id!) && "bg-aegold-50 text-aegold-900")}>
                  <m.icon className="size-3.5 shrink-0 text-sand-500" /><span className="truncate">{locale === "ar" && n.labelAr ? n.labelAr : n.label}</span>
                  {n.key === graph.initialState ? <span className="ms-auto rounded bg-success-100 px-1 text-[9px] font-semibold text-success-700">START</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-500">{title}</p>
      <p className="mb-1.5 text-[10.5px] leading-snug text-sand-500">{hint}</p>
      {children}
    </section>
  );
}
